import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { SettingsProvider } from "@/hooks/useSettings";
import TenantMeta from "@/components/TenantMeta";
import {
  getTenantSlugFromPath,
  getLegacyRedirectPath,
  tenantBasename,
} from "@/lib/tenant";

// Pages
import PublicHome from "@/pages/PublicHome";
import BookingWizard from "@/pages/BookingWizard";
import ManageBooking from "@/pages/ManageBooking";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import Appointments from "@/pages/Appointments";
import Clients from "@/pages/Clients";
import ClientProfile from "@/pages/ClientProfile";
import Services from "@/pages/Services";
import Staff from "@/pages/Staff";
import Inventory from "@/pages/Inventory";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import ImportWizard from "@/pages/ImportWizard";
import DuplicateMerge from "@/pages/DuplicateMerge";
import AIAssistantPage from "@/pages/AIAssistantPage";
import ConsultationFormPage from "@/pages/ConsultationFormPage";
import BuyGiftVoucher from "@/pages/BuyGiftVoucher";
import EmailAutomations from "@/pages/EmailAutomations";
import Vouchers from "@/pages/Vouchers";
import HelpDesk from "@/pages/HelpDesk";
import SuperAdminSalons from "@/pages/SuperAdminSalons";
import ReviewsPage from "@/pages/ReviewsPage";
import AdminReviews from "@/pages/AdminReviews";
import StaffTargets from "@/pages/StaffTargets";
import ComingSoon from "@/pages/ComingSoon";
import Competitions from "@/pages/Competitions";
import CompetitionEdit from "@/pages/CompetitionEdit";
import CompetitionPage from "@/pages/CompetitionPage";
import SharingTools from "@/pages/SharingTools";
import AdminLayout from "@/components/AdminLayout";

// API is always served from the same origin (Kubernetes ingress proxies /api → backend).
export const API = `${window.location.origin}/api`;

// ── Path-based multi-tenancy bootstrap ──
// 0a. Vanity-domain redirect — if the registrar 301 didn't catch it, redirect
//     `mamanails.co.uk[/path]` → `bookdbeyond.com/mamabeauty[/path]` here. This
//     only fires if mamanails.co.uk has been wired as an Emergent custom domain
//     pointing at this deployment AND the registrar forward isn't intercepting.
// 0b. Legacy URL redirect — if the user clicked an old email link like
//     /manage/{token} or /book/mama-nails, rewrite to the default-tenant
//     equivalent BEFORE we read the slug.
// 1.  Resolve tenant slug from URL path.
// 2.  Pin slug to an axios interceptor so every /api call carries X-Tenant-Slug.
// 3.  Mount BrowserRouter with basename=/<slug>.
const VANITY_HOST_TO_TENANT = {
  'mamanails.co.uk': 'mamabeauty',
  'www.mamanails.co.uk': 'mamabeauty',
};

if (typeof window !== 'undefined') {
  // 0a. Vanity host → canonical bookdbeyond.com/<slug> redirect.
  const vanitySlug = VANITY_HOST_TO_TENANT[window.location.hostname.toLowerCase()];
  if (vanitySlug) {
    const target =
      `https://bookdbeyond.com/${vanitySlug}` +
      window.location.pathname +
      window.location.search +
      window.location.hash;
    window.location.replace(target);
    // eslint-disable-next-line no-throw-literal
    throw new Error('vanity-redirect');
  }

  // 0b. Legacy /manage/* and /book/* redirects.
  const legacy = getLegacyRedirectPath(window.location.pathname);
  if (legacy) {
    window.location.replace(legacy + window.location.search + window.location.hash);
    // eslint-disable-next-line no-throw-literal
    throw new Error('legacy-redirect');
  }
}

const TENANT_SLUG = getTenantSlugFromPath();
const ROUTER_BASENAME = tenantBasename(TENANT_SLUG);

// Global axios interceptor — adds X-Tenant-Slug to every request.
axios.interceptors.request.use((config) => {
  if (TENANT_SLUG) {
    config.headers = config.headers || {};
    if (!config.headers['X-Tenant-Slug']) {
      config.headers['X-Tenant-Slug'] = TENANT_SLUG;
    }
  }
  return config;
});

// Initialize seed data once per tab.
const initSeed = async () => {
  try {
    await axios.post(`${API}/seed`);
  } catch {
    // Seed endpoint returns 409 if already seeded
  }
};

// Protected route wrapper — redirects to /login (within the tenant basename).
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl mx-auto mb-3 bg-primary/10 flex items-center justify-center text-primary font-bold animate-pulse">
            B
          </div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return children;
}

function AppRoutes() {
  useEffect(() => {
    initSeed();
  }, []);

  return (
    <Routes>
      {/* Public routes — paths are relative to the BrowserRouter basename
          (i.e. `/{tenant_slug}`), so `/` here resolves to `/{slug}/`. */}
      <Route path="/" element={<PublicHome />} />
      <Route path="/book" element={<BookingWizard />} />
      {/* Backwards-compat: old URLs were /book/:salonSlug — strip the slug. */}
      <Route path="/book/:salonSlug" element={<BookingWizard />} />
      <Route path="/manage/:token" element={<ManageBooking />} />
      <Route path="/consultation" element={<ConsultationFormPage />} />
      <Route path="/gift-vouchers" element={<BuyGiftVoucher />} />
      <Route path="/reviews" element={<ReviewsPage />} />
      <Route path="/competition/:slug" element={<CompetitionPage />} />
      <Route path="/login" element={<LoginPage />} />

      {/* Admin routes — protected */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:clientId" element={<ClientProfile />} />
        <Route path="services" element={<Services />} />
        <Route path="staff" element={<Staff />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="reports" element={<Reports />} />
        <Route path="settings" element={<Settings />} />
        <Route path="import" element={<ImportWizard />} />
        <Route path="duplicates" element={<DuplicateMerge />} />
        <Route path="ai-assistant" element={<AIAssistantPage />} />
        <Route path="email-automations" element={<EmailAutomations />} />
        <Route path="helpdesk" element={<HelpDesk />} />
        <Route path="vouchers" element={<Vouchers />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="competitions" element={<Competitions />} />
        <Route path="competitions/:id" element={<CompetitionEdit />} />
        <Route path="sharing-tools" element={<SharingTools />} />
        <Route path="targets" element={<StaffTargets />} />
        <Route path="super-admin/salons" element={<SuperAdminSalons />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const BEYOND_MEASURE_LOGO = "https://customer-assets.emergentagent.com/job_a2ffd257-50b9-4530-8932-b0e03417ee6f/artifacts/7jim1jy4_icon-transparent-88680f9.png";

function PoweredBy() {
  const location = useLocation();
  const isBooking = location.pathname.startsWith('/book');

  if (isBooking) return null;

  return (
    <a
      href="https://www.beyondmeasure.global"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="powered-by-badge"
      className="fixed bottom-3 right-3 z-50 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition-all duration-200 no-underline"
      style={{
        background: 'rgba(0,0,0,0.55)',
        border: '1px solid rgba(255,255,255,0.10)',
        backdropFilter: 'blur(6px)',
        color: 'rgba(255,255,255,0.85)',
        textDecoration: 'none',
      }}
    >
      <img src={BEYOND_MEASURE_LOGO} alt="Beyond Measure" className="w-4 h-4 rounded-full object-cover" />
      <span style={{ fontSize: '9px', fontWeight: 500, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
        Powered by Beyond Measure
      </span>
    </a>
  );
}

// "Unknown tenant" page — shown when the URL slug exists but doesn't match
// any active tenant (e.g. typo: /laurenaesthetics instead of /lauren-aesthetics).
function UnknownTenant({ slug }) {
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    axios.get(`${API}/tenants/public`)
      .then(res => {
        // Best-effort fuzzy match: any tenant slug containing the typed string
        // (or vice-versa), case-insensitive, hyphens collapsed.
        const norm = (s) => (s || '').toLowerCase().replace(/-/g, '');
        const want = norm(slug);
        const all = res.data || [];
        const matches = all.filter(t => {
          const ts = norm(t.slug);
          return ts === want || ts.includes(want) || want.includes(ts);
        });
        setSuggestions(matches.length ? matches : all);
      })
      .catch(() => setSuggestions([]));
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="unknown-tenant">
      <div className="max-w-md w-full text-center">
        <h1 className="text-2xl font-semibold mb-2">Salon not found</h1>
        <p className="text-sm text-muted-foreground mb-6">
          We couldn't find a salon at <code className="px-1.5 py-0.5 bg-muted rounded text-xs">/{slug}</code>.
          {suggestions.length > 0 && ' Did you mean one of these?'}
        </p>
        {suggestions.length > 0 && (
          <ul className="space-y-2 mb-6">
            {suggestions.map(t => (
              <li key={t.slug}>
                <a
                  href={`/${t.slug}`}
                  data-testid={`unknown-tenant-suggestion-${t.slug}`}
                  className="block px-4 py-3 rounded-xl border border-border hover:border-primary hover:bg-muted/40 transition-colors text-left"
                >
                  <span className="font-medium">{t.display_name}</span>
                  <span className="block text-xs text-muted-foreground">/{t.slug}</span>
                </a>
              </li>
            ))}
          </ul>
        )}
        <a
          href="/"
          className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
          data-testid="unknown-tenant-home-link"
        >
          ← Back to the directory
        </a>
      </div>
    </div>
  );
}

function TenantBoundary({ children }) {
  // Validates the URL slug against the public tenant list. Renders an
  // "Unknown tenant" page when the slug isn't a real salon (e.g. typo).
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'unknown'

  useEffect(() => {
    if (!TENANT_SLUG) {
      setStatus('ok');
      return;
    }
    axios.get(`${API}/tenants/resolve/${TENANT_SLUG}`)
      .then(() => setStatus('ok'))
      .catch(() => setStatus('unknown'));
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground" data-testid="tenant-boundary-loading">Loading…</p>
      </div>
    );
  }
  if (status === 'unknown') {
    return <UnknownTenant slug={TENANT_SLUG} />;
  }
  return children;
}

function App() {
  // When there's no tenant slug resolvable from the URL, show the B∞kd
  // marketing/coming-soon landing page at the SaaS root. (Previously this
  // auto-redirected to /mamabeauty, which leaked Mama Beauty branding to
  // anyone landing on bookdbeyond.com/.)
  if (!TENANT_SLUG) {
    return <ComingSoon />;
  }

  return (
    <SettingsProvider>
      <TenantMeta />
      <AuthProvider>
        <BrowserRouter basename={ROUTER_BASENAME}>
          <TenantBoundary>
            <AppRoutes />
            <PoweredBy />
          </TenantBoundary>
        </BrowserRouter>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
